import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Stack,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Notes as NotesIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Lightbulb as LightbulbIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Schedule as ScheduleIcon,
  Hotel as HotelIcon,
  Restaurant as RestaurantIcon,
  Event as EventIcon,
  DirectionsCar as TransportIcon,
  LocalActivity as ActivityIcon,
  Place as PlaceIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSuggestions } from '../hooks/useSuggestions';
import { useGlobalWebSocket } from '../contexts/GlobalWebSocketContext';
import { useAuth } from '../contexts/AuthContext';
import LocationAutocomplete from './LocationAutocomplete';
import DateRangePicker from './DateRangePicker';
import { 
  THEME_RADIUS, 
  THEME_COLORS, 
  THEME_GRADIENTS, 
  THEME_BORDERS, 
  THEME_SHADOWS, 
  THEME_TRANSITIONS,
  COMMON_STYLES
} from '../constants/theme';
import { DOCUMENT, documentAddButtonSx, documentEyebrowSx, documentBadgeSx, getDocumentStatusBadgeSx } from '../constants/documentTheme';
import DocumentSectionHead from './DocumentSectionHead';
import { parsePartnerRecommendationMeta } from '../utils/partnerRecommendation';

// Professional category colors using theme constants
const categoryColors = {
  hotel: THEME_COLORS.categories.hotel.main,
  restaurant: THEME_COLORS.categories.restaurant.main,
  event: THEME_COLORS.categories.event.main,
  transport: THEME_COLORS.categories.transport.main,
  activity: THEME_COLORS.secondary.main,
  other: THEME_COLORS.text.secondary
};

const categoryIcons = {
  hotel: <HotelIcon />,
  restaurant: <RestaurantIcon />,
  event: <EventIcon />,
  transport: <TransportIcon />,
  activity: <ActivityIcon />,
  other: <PlaceIcon />
};

// Professional gradients using theme constants
const categoryGradients = {
  hotel: THEME_COLORS.categories.hotel.gradient,
  restaurant: THEME_COLORS.categories.restaurant.gradient,
  event: THEME_COLORS.categories.event.gradient,
  transport: THEME_COLORS.categories.transport.gradient,
  activity: THEME_COLORS.categories.activity.gradient,
  other: THEME_COLORS.categories.other.gradient
};

// Safe image component with error handling
const SafeImage = ({ src, alt, sx, ...props }) => {
  const [imgSrc, setImgSrc] = useState(src);
  const [hasError, setHasError] = useState(false);

  const handleError = () => {
    if (!hasError) {
      setHasError(true);
      // Fallback to a simple gradient background
      setImgSrc(null);
    }
  };

  useEffect(() => {
    setImgSrc(src);
    setHasError(false);
  }, [src]);

  if (hasError || !imgSrc) {
    return (
      <Box
        sx={{
          ...sx,
          background: THEME_GRADIENTS.glass,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: THEME_COLORS.text.secondary,
        }}
        {...props}
      >
        <LocationIcon sx={{ fontSize: 40, opacity: 0.5 }} />
      </Box>
    );
  }

  return (
    <CardMedia
      component="img"
      src={imgSrc}
      alt={alt}
      onError={handleError}
      sx={sx}
      {...props}
    />
  );
};

// Function to get image URL with fallback (same as itinerary)
const getImageUrl = (location, category) => {
  // Category-specific seed values for consistent images
  const categorySeeds = {
    hotel: 100,
    restaurant: 200,
    event: 300,
    transport: 400,
    activity: 500,
    other: 600
  };
  
  const seed = categorySeeds[category] || 600;
  // Use location hash to generate consistent images for same locations
  const locationSeed = location ? 
    location.split('').reduce((a, b) => a + b.charCodeAt(0), 0) % 100 : 0;
  
  const finalSeed = seed + locationSeed;
  
  // Use Picsum for reliable placeholder images
  return `https://picsum.photos/seed/${finalSeed}/400/200`;
};

const TripSuggestions = ({ tripId, trip, isCreator, isPartner = false, onItineraryUpdate, onSwitchToItinerary, onOpenSuggestionDialog }) => {
  const { t } = useTranslation();
  const { addEventListener, subscribeToTrip, unsubscribeFromTrip } = useGlobalWebSocket();
  const { user } = useAuth();
  const {
    suggestions,
    loading,
    error,
    fetchSuggestions,
    addSuggestion,
    updateSuggestion,
    updateSuggestionStatus,
    deleteSuggestion,
    vote,
    addDiscussion,
    deleteDiscussion,
  } = useSuggestions(tripId, { usePartnerAuth: isPartner });
  const isGroupTrip = Boolean(trip?.master_trip_id || Number(trip?.is_master_trip));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSuggestion, setEditingSuggestion] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    google_map_link: '',
    category: 'other',
    description: '',
    date: '',
    time: '',
    preferred_date: '',
    preferred_time: '',
    preferred_end_date: '',
    preferred_end_time: '',
  });
  const [discussionDialogs, setDiscussionDialogs] = useState({});
  const [newDiscussionMessage, setNewDiscussionMessage] = useState('');

  // Function to open suggestion dialog with pre-filled data
  const openSuggestionDialog = useCallback((activityData) => {
    setFormData({
      name: activityData.name || '',
      location: activityData.location || '',
      google_map_link: activityData.google_map_link || '',
      category: activityData.category || 'other',
      description: activityData.description || '',
      date: '',
      time: '',
      preferred_date: '',
      preferred_time: '',
      preferred_end_date: '',
      preferred_end_time: '',
    });
    setDialogOpen(true);
    setEditingSuggestion(null);
  }, []);

  // Expose the function to parent component
  useEffect(() => {
    if (onOpenSuggestionDialog) {
      onOpenSuggestionDialog(openSuggestionDialog);
    }
  }, [onOpenSuggestionDialog, openSuggestionDialog]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions, tripId]);

  // Subscribe to trip for real-time updates
  useEffect(() => {
    if (subscribeToTrip) {
      subscribeToTrip(tripId);
    }
    return () => {
      if (unsubscribeFromTrip) {
        unsubscribeFromTrip(tripId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]); // Only depend on tripId to prevent infinite subscription loops

  // WebSocket event listeners for real-time updates
  useEffect(() => {
    if (!addEventListener) return;

    const handleSuggestionEvent = (data) => {
      if (String(data.trip_id) === String(tripId)) {
        fetchSuggestions(); // Refetch to get the latest data
      }
    };

    // Subscribe to all suggestion-related events
    const unsubscribers = [
      addEventListener('suggestion_added', handleSuggestionEvent),
      addEventListener('suggestion_status_updated', handleSuggestionEvent),
      addEventListener('suggestion_converted', handleSuggestionEvent),
      addEventListener('suggestion_deleted', handleSuggestionEvent),
      addEventListener('suggestion_discussion_added', handleSuggestionEvent),
      addEventListener('suggestion_vote_updated', handleSuggestionEvent),
    ];

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [addEventListener, tripId, fetchSuggestions]);

  const handleOpenDialog = (suggestion = null) => {
    if (suggestion) {
      setEditingSuggestion(suggestion);
      
      // Extract preferred_date and preferred_time from start_datetime if it exists
      let preferred_date = '';
      let preferred_time = '';
      let preferred_end_date = '';
      let preferred_end_time = '';
      
      if (suggestion.start_datetime) {
        const startDateTime = new Date(suggestion.start_datetime);
        preferred_date = startDateTime.toISOString().split('T')[0];
        preferred_time = startDateTime.toTimeString().slice(0, 5); // HH:MM format
      }
      
      if (suggestion.end_datetime) {
        const endDateTime = new Date(suggestion.end_datetime);
        preferred_end_date = endDateTime.toISOString().split('T')[0];
        preferred_end_time = endDateTime.toTimeString().slice(0, 5); // HH:MM format
      }
      
      setFormData({
        name: suggestion.name,
        location: suggestion.location,
        google_map_link: suggestion.google_map_link || '',
        category: suggestion.category,
        description: suggestion.description || '',
        date: suggestion.date || '',
        time: suggestion.time || '',
        preferred_date: preferred_date,
        preferred_time: preferred_time,
        preferred_end_date: preferred_end_date,
        preferred_end_time: preferred_end_time,
      });
    } else {
      setEditingSuggestion(null);
      setFormData({
        name: '',
        location: '',
        google_map_link: '',
        category: 'other',
        description: '',
        date: '',
        time: '',
        preferred_date: '',
        preferred_time: '',
        preferred_end_date: '',
        preferred_end_time: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSuggestion(null);
    setFormData({
      name: '',
      location: '',
      google_map_link: '',
      category: 'other',
      description: '',
      date: '',
      time: '',
      preferred_date: '',
      preferred_time: '',
      preferred_end_date: '',
      preferred_end_time: '',
    });
  };

  const handleSubmit = async () => {
    try {
      // Prepare the data for submission
      const submitData = { ...formData };
      
      // The backend expects start_datetime, so we need to create it from preferred_date and preferred_time
      // For suggestions, these are optional, so we'll create a reasonable default if they're not provided
      if (formData.preferred_date || formData.preferred_time) {
        const date = formData.preferred_date || new Date().toISOString().split('T')[0];
        const time = formData.preferred_time || '09:00';
        submitData.start_datetime = `${date}T${time}:00`;
      } else {
        // If no preferred timing is specified, use a default datetime (today at 9 AM)
        const today = new Date();
        const defaultDate = today.toISOString().split('T')[0];
        submitData.start_datetime = `${defaultDate}T09:00:00`;
      }
      
      // Handle end_datetime if both end date and time are provided
      if (formData.preferred_end_date && formData.preferred_end_time) {
        submitData.end_datetime = `${formData.preferred_end_date}T${formData.preferred_end_time}:00`;
      } else if (formData.preferred_date && formData.preferred_time) {
        // If only start time is provided, set end time 1 hour later as default (same day)
        const [hours, minutes] = formData.preferred_time.split(':').map(Number);
        let endHours = hours + 1;
        let endDate = formData.preferred_date;
        
        // If adding 1 hour goes past midnight, cap at 11:45 PM same day for suggestions
        if (endHours >= 24) {
          endHours = 23;
          const endMinutes = 45;
          submitData.end_datetime = `${endDate}T${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}:00`;
        } else {
          submitData.end_datetime = `${endDate}T${String(endHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
        }
      }
      
      // Remove the preferred_date and preferred_time fields as backend doesn't expect them
      delete submitData.preferred_date;
      delete submitData.preferred_time;
      delete submitData.preferred_end_date;
      delete submitData.preferred_end_time;

      if (editingSuggestion) {
        await updateSuggestion(editingSuggestion.id, submitData);
      } else {
        await addSuggestion(submitData);
      }
      handleCloseDialog();
      fetchSuggestions();
    } catch (err) {
      console.error('Error saving suggestion:', err);
    }
  };

  const handleVote = async (suggestionId, voteType) => {
    try {
      await vote(suggestionId, voteType);
      fetchSuggestions();
    } catch (err) {
      console.error('Error voting on suggestion:', err);
    }
  };

  const handleStatusUpdate = async (suggestionId, status) => {
    try {
      await updateSuggestionStatus(suggestionId, status, status === 'approved');
      fetchSuggestions();
      
      // If converting to itinerary, refresh itinerary and switch tabs
      if (status === 'approved' && onItineraryUpdate && onSwitchToItinerary) {
        setTimeout(() => {
          onItineraryUpdate();
          onSwitchToItinerary();
        }, 500);
      }
    } catch (err) {
      console.error('Error updating suggestion status:', err);
    }
  };

  const handleDelete = async (suggestionId) => {
    if (window.confirm('Are you sure you want to delete this suggestion?')) {
      try {
        await deleteSuggestion(suggestionId);
        fetchSuggestions();
      } catch (err) {
        console.error('Error deleting suggestion:', err);
      }
    }
  };

  const handleAddDiscussion = async (suggestionId) => {
    if (!newDiscussionMessage.trim()) return;
    
    try {
      await addDiscussion(suggestionId, newDiscussionMessage.trim());
      setNewDiscussionMessage('');
      setDiscussionDialogs(prev => ({ ...prev, [suggestionId]: false }));
      fetchSuggestions();
    } catch (err) {
      console.error('Error adding discussion message:', err);
    }
  };

  const handleDeleteDiscussion = async (suggestionId, messageId) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await deleteDiscussion(suggestionId, messageId);
        fetchSuggestions();
      } catch (err) {
        console.error('Error deleting discussion message:', err);
      }
    }
  };

  const getStatusText = (status, isPartnerRecommend = false) => {
    if (isPartnerRecommend) {
      switch (status) {
        case 'approved':
          return t('partner.opsHome.suggestionApproved');
        case 'rejected':
          return t('partner.opsHome.suggestionDeclined');
        default:
          return t('partner.opsHome.suggestionAwaiting');
      }
    }
    switch (status) {
      case 'approved':
        return 'Added to Itinerary';
      case 'rejected':
        return 'Rejected';
      default:
        return 'Under Review';
    }
  };

  const canEditSuggestion = (suggestion) => {
    return suggestion.suggested_by === user?.id;
  };

  // On a group trip only the organizer reviews; the API says so per suggestion.
  const canReviewSuggestion = (suggestion) => suggestion.can_review ?? isCreator;

  const canDeleteSuggestion = (suggestion) => {
    // Allow both the reviewer and the suggestion creator to delete
    return canReviewSuggestion(suggestion) || suggestion.suggested_by === user?.id;
  };

  const displayDescription = (description, isPartnerRecommend) => {
    if (!description) return '';
    if (!isPartnerRecommend) return description;
    return String(description)
      .split('\n')
      .filter((line) => {
        const trimmed = line.trim();
        if (!trimmed) return false;
        if (/recommended by your lifestyle manager/i.test(trimmed)) return false;
        if (/^Price:\s*[A-Z]{3}\s*[0-9]/i.test(trimmed)) return false;
        return true;
      })
      .join('\n')
      .trim();
  };

  const renderSuggestion = (suggestion) => {
    // Use the vote data structure that the backend actually returns
    const userVote = suggestion.user_vote; // 'up', 'down', or null
    const upvotes = suggestion.upvotes || 0;
    const downvotes = suggestion.downvotes || 0;
    const partnerMeta = parsePartnerRecommendationMeta(suggestion.description);
    const isPartnerRecommend = partnerMeta.isPartnerRecommend;
    const notesBody = displayDescription(suggestion.description, isPartnerRecommend);
    
    return (
      <Card
        key={suggestion.id}
        elevation={0}
        sx={{
          mb: 0,
          overflow: 'visible',
          border: 0,
          borderRadius: 0,
          borderBottom: `1px solid ${DOCUMENT.rule}`,
          boxShadow: 'none',
          bgcolor: 'transparent',
        }}
        data-testid={isPartnerRecommend ? 'partner-recommend-card' : 'trip-suggestion-card'}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', py: 1.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <CardContent sx={{ flex: 1, pb: 1, px: 0, pt: 0 }}>
              <Typography sx={{ ...documentEyebrowSx, mb: 0.5 }}>
                {suggestion.category}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1, gap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
                  <Typography component="h4" sx={{ fontFamily: DOCUMENT.display, fontSize: 18, fontWeight: 400 }}>
                    {suggestion.name}
                  </Typography>
                  
                  {/* Category and Status badges */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                    {isPartnerRecommend && (
                      <Box
                        component="span"
                        data-testid="partner-recommend-badge"
                        sx={{
                          ...documentBadgeSx,
                          backgroundColor: '#C9A961',
                          color: '#2A070C',
                          border: 0,
                        }}
                      >
                        {t('partner.opsHome.fromYourAgent')}
                      </Box>
                    )}
                    <Box component="span" sx={documentBadgeSx}>
                      {categoryIcons[suggestion.category]}
                      {suggestion.category}
                    </Box>
                    <Box component="span" sx={getDocumentStatusBadgeSx(suggestion.status)}>
                      {getStatusText(suggestion.status, isPartnerRecommend)}
                    </Box>
                    {isPartnerRecommend && partnerMeta.cost != null && (
                      <Box
                        component="span"
                        data-testid="partner-recommend-price"
                        sx={{
                          ...documentBadgeSx,
                          borderColor: DOCUMENT.accent,
                          color: DOCUMENT.ink,
                        }}
                      >
                        {partnerMeta.cost_currency} {Number(partnerMeta.cost).toFixed(2)}
                      </Box>
                    )}
                  </Box>
                </Box>
                
                {/* Edit and Delete Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1,
                  alignItems: 'flex-start'
                }}>
                  {/* Edit for suggestion owner only */}
                  {canEditSuggestion(suggestion) && (
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(suggestion)}
                      sx={{ 
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        bgcolor: 'rgba(25, 118, 210, 0.1)',
                        color: 'primary.main',
                        border: '1px solid rgba(25, 118, 210, 0.3)',
                        '&:hover': { 
                          bgcolor: 'rgba(25, 118, 210, 0.2)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                  
                  {/* Delete for trip owner or suggestion creator */}
                  {canDeleteSuggestion(suggestion) && (
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(suggestion.id)}
                      sx={{ 
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        bgcolor: 'rgba(244, 67, 54, 0.1)',
                        color: 'error.main',
                        border: '1px solid rgba(244, 67, 54, 0.3)',
                        '&:hover': { 
                          bgcolor: 'rgba(244, 67, 54, 0.2)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)'
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>

              {/* Action Buttons for Owners - Below Title */}
              {canReviewSuggestion(suggestion) && suggestion.status === 'pending' && (
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  flexDirection: { xs: 'row', sm: 'row' },
                  flexWrap: 'wrap',
                  mb: 2
                }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon fontSize="small" />}
                    onClick={() => handleStatusUpdate(suggestion.id, 'approved')}
                    sx={{ 
                      fontSize: { xs: 10, sm: 11 },
                      whiteSpace: 'nowrap',
                      px: 1.5,
                      py: 0.5,
                      minWidth: 'auto',
                      textTransform: 'none'
                    }}
                  >
                    {isPartnerRecommend
                      ? t('partner.opsHome.approveRecommend')
                      : 'Add to Itinerary'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon fontSize="small" />}
                    onClick={() => handleStatusUpdate(suggestion.id, 'rejected')}
                    sx={{ 
                      fontSize: { xs: 10, sm: 11 },
                      whiteSpace: 'nowrap',
                      px: 1.5,
                      py: 0.5,
                      minWidth: 'auto',
                      textTransform: 'none'
                    }}
                  >
                    {isPartnerRecommend
                      ? t('partner.opsHome.declineRecommend')
                      : 'Reject'}
                  </Button>
                </Box>
              )}

              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 1, 
                gap: { xs: 1, sm: 2 }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                    {suggestion.location}
                  </Typography>
                  {suggestion.google_map_link && (
                    <IconButton
                      size="small"
                      onClick={() => window.open(suggestion.google_map_link, '_blank')}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                
                {/* Preferred timing display */}
                {suggestion.start_datetime && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScheduleIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                      {format(new Date(suggestion.start_datetime), 'MMM d, yyyy \'at\' h:mm a')} (suggested)
                    </Typography>
                  </Box>
                )}
              </Box>
              
              {notesBody && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {notesBody}
                </Typography>
              )}

              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between', 
                mb: 1,
                gap: { xs: 1, sm: 0 }
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
                  {isPartnerRecommend
                    ? t('partner.opsHome.recommendedByOn', {
                      name: suggestion.suggester_name,
                      date: format(new Date(suggestion.created_at), 'MMM d, yyyy'),
                    })
                    : `Suggested by ${suggestion.suggester_name} on ${format(new Date(suggestion.created_at), 'MMM d, yyyy')}`}
                </Typography>
                
                {/* Voting Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, alignSelf: { xs: 'flex-end', sm: 'auto' } }}>
                  <Button
                    size="small"
                    variant={userVote === 'up' ? 'contained' : 'outlined'}
                    color="success"
                    startIcon={<ThumbUpIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                    onClick={() => handleVote(suggestion.id, 'up')}
                    sx={{ 
                      fontSize: { xs: 11, sm: 12 },
                      minWidth: 'auto', 
                      px: { xs: 1, sm: 1.5 },
                      py: { xs: 0.5, sm: 0.75 },
                      borderRadius: THEME_RADIUS.small
                    }}
                  >
                    {upvotes}
                  </Button>
                  <Button
                    size="small"
                    variant={userVote === 'down' ? 'contained' : 'outlined'}
                    color="error"
                    startIcon={<ThumbDownIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                    onClick={() => handleVote(suggestion.id, 'down')}
                    sx={{ 
                      fontSize: { xs: 11, sm: 12 },
                      minWidth: 'auto', 
                      px: { xs: 1, sm: 1.5 },
                      py: { xs: 0.5, sm: 0.75 },
                      borderRadius: THEME_RADIUS.small
                    }}
                  >
                    {downvotes}
                  </Button>
                </Box>
              </Box>
            </CardContent>

            {/* Discussion Section */}
            {suggestion.discussions && suggestion.discussions.length > 0 && (
              <Box sx={{ px: 2, pb: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <NotesIcon sx={{ mr: 0.5, fontSize: 16 }} />
                  Discussion ({suggestion.discussions.length})
                </Typography>
                <List dense sx={{ py: 0 }}>
                  {suggestion.discussions.slice(0, 2).map((discussion) => (
                    <ListItem key={discussion.id} sx={{ px: 0, py: 0.5 }}>
                      <ListItemAvatar sx={{ minWidth: 32 }}>
                        <Avatar src={discussion.avatar} sx={{ width: 24, height: 24, fontSize: 12 }}>
                          {discussion.username?.[0]?.toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={discussion.message}
                        secondary={`${discussion.username} • ${format(new Date(discussion.created_at), 'MMM d, h:mm a')}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      {discussion.user_id === user?.id && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteDiscussion(suggestion.id, discussion.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </ListItem>
                  ))}
                  {suggestion.discussions.length > 2 && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
                      +{suggestion.discussions.length - 2} more messages
                    </Typography>
                  )}
                </List>
              </Box>
            )}

            {/* Add Discussion Button */}
            <Box sx={{ px: 2, pb: 2 }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDiscussionDialogs(prev => ({ ...prev, [suggestion.id]: true }))}
                sx={{ 
                  fontSize: 12,
                  backgroundColor: THEME_COLORS.primary.main,
                  color: 'white',
                  textTransform: 'none',
                  borderRadius: THEME_RADIUS.small,
                  '&:hover': {
                    backgroundColor: THEME_COLORS.primary.dark,
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': {
                    backgroundColor: THEME_COLORS.text.disabled,
                    color: 'white',
                    opacity: 0.6
                  }
                }}
              >
                Add Comment
              </Button>
            </Box>
          </Box>
        </Box>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show loading if trip data is not fully loaded
  if (!trip) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show loading state from useSuggestions hook
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading suggestions...</Typography>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', flexDirection: 'column' }}>
        <Typography color="error" sx={{ mb: 2 }}>Error: {error}</Typography>
        <Button variant="outlined" onClick={fetchSuggestions}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <DocumentSectionHead
        title="Suggestions"
        meta="From explore"
        actions={
          <Button onClick={() => handleOpenDialog()} sx={{ ...documentAddButtonSx, mt: 0, ml: 'auto' }}>
            New Suggestion
          </Button>
        }
      />

      {isGroupTrip && (
        <Typography
          data-testid="group-suggestions-note"
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          {t('itinerary.groupSuggestionsShared')}
        </Typography>
      )}

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: THEME_RADIUS.small,
            ...COMMON_STYLES.professionalCard
          }}
        >
          {error}
        </Alert>
      )}

      {suggestions.length === 0 ? (
        <Paper 
          sx={{ 
            p: 6, 
            textAlign: 'center', 
            borderRadius: THEME_RADIUS.small,
            background: THEME_GRADIENTS.glass,
            border: THEME_BORDERS.light,
            position: 'relative',
            overflow: 'hidden',
            ...COMMON_STYLES.professionalCard,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              background: `radial-gradient(circle, ${THEME_COLORS.primary.main}08 0%, transparent 70%)`,
              animation: 'pulse 4s ease-in-out infinite'
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: THEME_RADIUS.small,
                background: THEME_GRADIENTS.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                boxShadow: THEME_SHADOWS.medium
              }}
            >
              <LightbulbIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700, 
                mb: 2,
                color: THEME_COLORS.text.primary
              }}
            >
              No Suggestions Yet
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                color: THEME_COLORS.text.secondary,
                mb: 4,
                maxWidth: 400,
                mx: 'auto'
              }}
            >
              Be the first to suggest an amazing activity! Your ideas help create unforgettable experiences for everyone.
            </Typography>
            
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                ...COMMON_STYLES.professionalButton,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Make First Suggestion
            </Button>
          </Box>
        </Paper>
      ) : (
        <Stack spacing={3}>
          {suggestions.map(renderSuggestion)}
        </Stack>
      )}

      {/* Add/Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog} 
        maxWidth="lg" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: THEME_RADIUS.small,
            maxHeight: '90vh',
            boxShadow: THEME_SHADOWS.heavy,
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            backgroundColor: THEME_COLORS.primary.main,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 3,
            px: 4
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: THEME_RADIUS.small,
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {editingSuggestion ? <EditIcon sx={{ color: 'white' }} /> : <LightbulbIcon sx={{ color: 'white' }} />}
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, color: 'white' }}>
              {editingSuggestion ? 'Edit Suggestion' : 'Suggest New Activity'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, color: 'white' }}>
              {editingSuggestion ? 'Update your suggestion details' : 'Share your amazing idea with the group'}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, overflow: 'auto' }}>
          <Grid container>
            {/* Left Panel - Form */}
            <Grid item xs={12} md={8} sx={{ p: 4 }}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ActivityIcon sx={{ color: '#ff6b35' }} />
                  Suggestion Details
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Activity Name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      placeholder={editingSuggestion ? "Update the activity name" : "What amazing activity are you suggesting?"}
                      InputProps={{
                        startAdornment: (
                          editingSuggestion ? <EditIcon sx={{ color: 'action.active', mr: 1 }} /> : <LightbulbIcon sx={{ color: 'action.active', mr: 1 }} />
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: THEME_RADIUS.small,
                          '&:hover fieldset': {
                            borderColor: '#ff6b35',
                          }
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Activity Category</InputLabel>
                      <Select
                        value={formData.category}
                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                        label="Activity Category"
                        startAdornment={categoryIcons[formData.category]}
                        sx={{
                          borderRadius: THEME_RADIUS.small,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: THEME_RADIUS.small,
                          }
                        }}
                      >
                        <MenuItem value="hotel">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: categoryColors.hotel, width: 32, height: 32 }}>
                              🏨
                            </Avatar>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>Hotel</Typography>
                              <Typography variant="caption" color="text.secondary">Accommodation & lodging</Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                        <MenuItem value="restaurant">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: categoryColors.restaurant, width: 32, height: 32 }}>
                              🍽️
                            </Avatar>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>Restaurant</Typography>
                              <Typography variant="caption" color="text.secondary">Dining & food experiences</Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                        <MenuItem value="event">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: categoryColors.event, width: 32, height: 32 }}>
                              🎉
                            </Avatar>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>Event</Typography>
                              <Typography variant="caption" color="text.secondary">Shows, concerts & celebrations</Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                        <MenuItem value="transport">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: categoryColors.transport, width: 32, height: 32 }}>
                              🚗
                            </Avatar>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>Transport</Typography>
                              <Typography variant="caption" color="text.secondary">Travel & commuting</Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                        <MenuItem value="activity">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: categoryColors.activity, width: 32, height: 32 }}>
                              🎯
                            </Avatar>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>Activity</Typography>
                              <Typography variant="caption" color="text.secondary">Tours, attractions & adventures</Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                        <MenuItem value="other">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: categoryColors.other, width: 32, height: 32 }}>
                              📍
                            </Avatar>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>Other</Typography>
                              <Typography variant="caption" color="text.secondary">Miscellaneous activities</Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon sx={{ color: '#ff6b35' }} />
                  Location & Venue
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <LocationAutocomplete
                      value={formData.location}
                      onChange={(newValue) => setFormData(prev => ({ ...prev, location: newValue }))}
                      onPlaceSelect={(placeData) => {
                        setFormData(prev => ({
                          ...prev,
                          location: placeData.address,
                          google_map_link: placeData.googleMapsLink
                        }));
                      }}
                      label={editingSuggestion ? "Update the location" : "Where is this suggestion located?"}
                      required
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: THEME_RADIUS.small,
                        }
                      }}
                    />
                  </Grid>
                  
                  {formData.google_map_link && (
                    <Grid item xs={12}>
                      <Paper 
                        sx={{ 
                          p: 2, 
                          bgcolor: 'success.main', 
                          color: 'white', 
                          borderRadius: THEME_RADIUS.small,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <LocationIcon />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Google Maps link automatically added
                        </Typography>
                        <IconButton 
                          size="small" 
                          sx={{ color: 'white', ml: 'auto' }}
                          onClick={() => window.open(formData.google_map_link, '_blank')}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon sx={{ color: '#ff6b35' }} />
                  Schedule & Timing (Optional)
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <DateRangePicker
                      label="Preferred Date & Time (Optional)"
                      startDateTime={formData.preferred_date && formData.preferred_time ? 
                        new Date(`${formData.preferred_date}T${formData.preferred_time}:00`) : null}
                      endDateTime={(() => {
                        const endDateTime = formData.preferred_end_date && formData.preferred_end_time ? 
                          new Date(`${formData.preferred_end_date}T${formData.preferred_end_time}:00`) : null;
                        return endDateTime;
                      })()}
onStartDateTimeChange={(dateTime) => {
                        if (dateTime) {
                          // Auto-set end time to one hour after start time
                          const endDateTime = new Date(dateTime);
                          endDateTime.setHours(endDateTime.getHours() + 1);
                          
                          // Round minutes to nearest valid option (0, 15, 30, 45)
                          const minutes = endDateTime.getMinutes();
                          const validMinutes = [0, 15, 30, 45];
                          const roundedMinutes = validMinutes.reduce((prev, curr) => 
                            Math.abs(curr - minutes) < Math.abs(prev - minutes) ? curr : prev
                          );
                          endDateTime.setMinutes(roundedMinutes);
                          
                          
                          setFormData(prev => {
                            const newData = {
                              ...prev,
                              preferred_date: format(dateTime, 'yyyy-MM-dd'),
                              preferred_time: format(dateTime, 'HH:mm'),
                              preferred_end_date: format(endDateTime, 'yyyy-MM-dd'),
                              preferred_end_time: format(endDateTime, 'HH:mm'),
                            };
                            return newData;
                          });
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            preferred_date: '',
                            preferred_time: '',
                            preferred_end_date: '',
                            preferred_end_time: '',
                          }));
                        }
                      }}
                      onEndDateTimeChange={(dateTime) => {
                        if (dateTime) {
                          setFormData(prev => ({
                            ...prev,
                            preferred_end_date: format(dateTime, 'yyyy-MM-dd'),
                            preferred_end_time: format(dateTime, 'HH:mm'),
                          }));
                        } else {
                          // Don't clear end date when it's null - user might be selecting start first
                          // Only clear if start date is also being cleared
                          if (!dateTime) {
                            setFormData(prev => ({
                              ...prev,
                              preferred_end_date: '',
                              preferred_end_time: '',
                            }));
                          }
                        }
                      }}
                      minDate={trip?.start_date ? new Date(trip.start_date + 'T00:00:00') : null}
                      maxDate={trip?.end_date ? new Date(trip.end_date + 'T23:45:59') : null}
                      showTimeSelection={true}
                      helperText={trip?.start_date && trip?.end_date ? 
                        `Suggestion must be within trip dates (${trip.start_date} - ${trip.end_date})` : 
                        'When would this activity be ideal? This helps the trip creator plan the schedule.'
                      }
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Paper 
                      sx={{ 
                        p: 2, 
                        bgcolor: '#fff3e0', 
                        border: '1px solid #ffcc8a',
                        borderRadius: THEME_RADIUS.small,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <ScheduleIcon sx={{ color: '#ff6b35' }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        <strong>Note:</strong> These are just suggestions for when this activity might work best. The trip creator will set the final schedule when adding it to the itinerary.
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NotesIcon sx={{ color: '#ff6b35' }} />
                  Why This Suggestion?
                </Typography>
                
                <TextField
                  fullWidth
                  label="Description & Reasoning"
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Why do you think this would be a great addition to the trip? What makes it special or interesting?"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: THEME_RADIUS.small,
                    }
                  }}
                />
              </Box>
            </Grid>

            {/* Right Panel - Preview */}
            <Grid item xs={12} md={4} sx={{ bgcolor: '#fef7f0', borderLeft: '1px solid #ffcc8a' }}>
              <Box sx={{ p: 4, height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  👁️ Preview
                </Typography>

                {/* Suggestion Preview Card */}
                <Paper 
                  sx={{ 
                    borderRadius: THEME_RADIUS.small, 
                    overflow: 'hidden',
                    border: formData.category ? `2px solid ${categoryColors[formData.category]}` : '2px solid #e2e8f0'
                  }}
                >
                  {/* Category Header */}
                  {formData.category && (
                    <Box
                      sx={{
                        background: categoryGradients[formData.category],
                        color: 'white',
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5
                      }}
                    >
                      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 32, height: 32 }}>
                        {categoryIcons[formData.category]}
                      </Avatar>
                      <Box>
                        <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 1, color: 'white' }}>
                          {formData.category}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'white' }}>
                          {formData.name || 'Suggestion Name'}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Preview Image */}
                  {formData.location && (
                    <SafeImage
                      src={getImageUrl(formData.location, formData.category)}
                      alt={formData.name}
                      sx={{ height: 120, objectFit: 'cover' }}
                    />
                  )}

                  <Box sx={{ p: 2 }}>
                    {/* Location */}
                    {formData.location && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          {formData.location.length > 30 ? `${formData.location.substring(0, 30)}...` : formData.location}
                        </Typography>
                      </Box>
                    )}

                    {/* Timing */}
                    {(formData.preferred_date || formData.preferred_time) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          {formData.preferred_date && format(new Date(formData.preferred_date), 'MMM d, yyyy')}
                          {formData.preferred_date && formData.preferred_time && ' at '}
                          {formData.preferred_time && format(new Date(`2000-01-01T${formData.preferred_time}`), 'h:mm a')}
                          {formData.preferred_date || formData.preferred_time ? ' (suggested)' : ''}
                        </Typography>
                      </Box>
                    )}

                    {/* Description Preview */}
                    {formData.description && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e2e8f0' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.4 }}>
                          {formData.description.length > 80 ? `${formData.description.substring(0, 80)}...` : formData.description}
                        </Typography>
                      </Box>
                    )}

                    {/* Suggestion Badge */}
                    <Box sx={{ mt: 2 }}>
                      <Chip
                        icon={<LightbulbIcon />}
                        label="Suggestion"
                        size="small"
                        sx={{
                          background: 'linear-gradient(135deg, #ff6b35 0%, #cc5529 100%)',
                          color: 'white',
                          fontWeight: 600,
                          '& .MuiChip-icon': { color: 'white' }
                        }}
                      />
                    </Box>
                  </Box>
                </Paper>

                {/* Completion Status */}
                <Paper sx={{ 
                  mt: 3, 
                  p: 2, 
                  borderRadius: THEME_RADIUS.small, 
                  bgcolor: THEME_COLORS.background.light,
                  border: THEME_BORDERS.light,
                  ...COMMON_STYLES.professionalCard
                }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Completion Status
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 12, 
                        height: 8, 
                        borderRadius: THEME_RADIUS.small, 
                        bgcolor: formData.name ? THEME_COLORS.status.success : THEME_COLORS.text.disabled
                      }} />
                      <Typography variant="caption" color={formData.name ? 'success.main' : 'text.secondary'}>
                        Activity name
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 12, 
                        height: 8, 
                        borderRadius: THEME_RADIUS.small, 
                        bgcolor: formData.location ? THEME_COLORS.status.success : THEME_COLORS.text.disabled
                      }} />
                      <Typography variant="caption" color={formData.location ? 'success.main' : 'text.secondary'}>
                        Location
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 12, 
                        height: 8, 
                        borderRadius: THEME_RADIUS.small, 
                        bgcolor: formData.description ? THEME_COLORS.status.success : THEME_COLORS.text.disabled
                      }} />
                      <Typography variant="caption" color={formData.description ? 'success.main' : 'text.secondary'}>
                        Description (recommended)
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 12, 
                        height: 8, 
                        borderRadius: THEME_RADIUS.small, 
                        bgcolor: (formData.preferred_date || formData.preferred_time) ? THEME_COLORS.accent.main : THEME_COLORS.text.disabled
                      }} />
                      <Typography variant="caption" color={(formData.preferred_date || formData.preferred_time) ? 'info.main' : 'text.secondary'}>
                        Preferred timing (optional)
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                {/* Suggestion Tips */}
                <Paper sx={{ mt: 3, p: 2, borderRadius: THEME_RADIUS.small, bgcolor: THEME_COLORS.primary.main, color: 'white' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'white' }}>
                    💡 Professional Tips
                  </Typography>
                  <Typography variant="caption" sx={{ lineHeight: 1.4, opacity: 0.9, color: 'white' }}>
                    • Provide clear and detailed information
                    <br />
                    • Include relevant business considerations
                    <br />
                    • Consider the group's professional requirements
                  </Typography>
                </Paper>

                {/* Voting Preview */}
                <Paper sx={{ mt: 3, p: 2, borderRadius: THEME_RADIUS.small, bgcolor: THEME_COLORS.background.light, border: THEME_BORDERS.light }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    🗳️ How Voting Works
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Chip
                      icon={<ThumbUpIcon />}
                      label="Like"
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem' }}
                    />
                    <Chip
                      icon={<ThumbDownIcon />}
                      label="Pass"
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                    Group members can vote and comment on your suggestion
                  </Typography>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions 
          sx={{ 
            p: { xs: 3, sm: 4 }, 
            pt: { xs: 2, sm: 2 },
            bgcolor: THEME_COLORS.background.light,
            borderTop: THEME_BORDERS.light,
            gap: { xs: 2, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' },
            '& .MuiButton-root': {
              minHeight: { xs: 48, sm: 44 },
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '1rem', sm: '0.875rem' }
            }
          }}
        >
          <Button 
            onClick={handleCloseDialog} 
            size="large"
            sx={{ 
              px: { xs: 4, sm: 4 },
              borderRadius: THEME_RADIUS.small,
              textTransform: 'none',
              fontWeight: 600,
              order: { xs: 2, sm: 1 }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            size="large"
            disabled={!formData.name || !formData.location}
            sx={{ 
              px: { xs: 4, sm: 4 },
              borderRadius: THEME_RADIUS.small,
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: THEME_COLORS.primary.main,
              color: 'white',
              order: { xs: 1, sm: 2 },
              '&:hover': {
                backgroundColor: THEME_COLORS.primary.dark,
                transform: 'translateY(-1px)',
                boxShadow: THEME_SHADOWS.medium
              },
              '&:disabled': {
                backgroundColor: THEME_COLORS.text.disabled,
                color: 'white',
                opacity: 0.6
              },
              transition: THEME_TRANSITIONS.default
            }}
          >
            {editingSuggestion ? (
              <>
                <EditIcon sx={{ mr: 1, color: 'inherit' }} />
                Update Suggestion
              </>
            ) : (
              <>
                <LightbulbIcon sx={{ mr: 1, color: 'inherit' }} />
                Share Suggestion
              </>
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discussion Dialogs */}
      {Object.entries(discussionDialogs).map(([suggestionId, open]) => (
        <Dialog
          key={suggestionId}
          open={open}
          onClose={() => setDiscussionDialogs(prev => ({ ...prev, [suggestionId]: false }))}
          maxWidth="sm"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: THEME_RADIUS.small,
              boxShadow: THEME_SHADOWS.heavy,
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: THEME_COLORS.primary.main,
            color: 'white',
            fontWeight: 700
          }}>
            Add Comment
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Comment"
              value={newDiscussionMessage}
              onChange={(e) => setNewDiscussionMessage(e.target.value)}
              sx={{ 
                mt: 1,
                ...COMMON_STYLES.professionalInput
              }}
              placeholder="Share your thoughts on this suggestion..."
            />
          </DialogContent>
          <DialogActions sx={{ 
            p: { xs: 3, sm: 3 }, 
            gap: { xs: 2, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' },
            '& .MuiButton-root': {
              minHeight: { xs: 48, sm: 44 },
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '1rem', sm: '0.875rem' }
            }
          }}>
            <Button 
              onClick={() => setDiscussionDialogs(prev => ({ ...prev, [suggestionId]: false }))}
              sx={{ 
                px: { xs: 3, sm: 3 },
                borderRadius: THEME_RADIUS.small,
                textTransform: 'none',
                fontWeight: 600,
                order: { xs: 2, sm: 1 }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleAddDiscussion(suggestionId)}
              variant="contained"
              disabled={!newDiscussionMessage.trim()}
              sx={{
                px: { xs: 3, sm: 3 },
                borderRadius: THEME_RADIUS.small,
                textTransform: 'none',
                fontWeight: 700,
                backgroundColor: THEME_COLORS.primary.main,
                color: 'white',
                order: { xs: 1, sm: 2 },
                '&:hover': {
                  backgroundColor: THEME_COLORS.primary.dark,
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  backgroundColor: THEME_COLORS.text.disabled,
                  color: 'white',
                  opacity: 0.6
                }
              }}
            >
              Add Comment
            </Button>
          </DialogActions>
        </Dialog>
      ))}
    </Box>
  );
};

export default TripSuggestions; 